// React
import type { RouteRecord } from 'vite-react-ssg';
import { Navigate } from 'react-router-dom';

// Services
import { getPages, type PageSlugType } from '@/services/pages/pagesService';
import { getNewsPageSlug } from '@/services/news-page/newsPageService';
import { getOffresEtServicesSlug } from '@/services/offres-et-services/offresStServicesService';

// Views
import ErrorFallback, { errorLoader } from '@/views/ErrorFallback/ErrorFallback';
import Home, { homePageLoader } from '@/views/Home/Home';
import News, { newsPageLoader } from '@/views/News/News';
import Contact, { contactLoader } from '@/views/Contact/Contact';
import OffresEtServices, { offresEtServicesLoader } from '@/views/OffresEtServices/OffresEtServices';
import { resources } from '@/locales/resources';
import App from '@/App';

// Utils
import { buildNestedRoutes } from '@/utils/buildNestedRoutes';
import { getContactSlug } from '@/utils/contactSlug';

/**
 * Slugs déjà couverts par des routes dédiées.
 * Les exclure évite un doublon de segment (ex. `actualites` sans composant) qui casse `/actualites`.
 */
function normalizeSlugPath(slug: string): string {
  return slug
    .replace(/^\/+|\/+$/g, '')
    .split('/')
    .filter(Boolean)
    .join('/');
}

function excludePagesConflictingWithStaticRoutes(
  pages: PageSlugType[],
  reservedSlugs: string[],
): PageSlugType[] {
  const reserved = reservedSlugs
    .map(normalizeSlugPath)
    .filter(Boolean);

  return pages.filter((page) => {
    const norm = normalizeSlugPath(page.slug);
    return !reserved.some(
      (res) => norm === res || norm.startsWith(`${res}/`),
    );
  });
}

const getChildren = async (): Promise<RouteRecord[]> => {
  const languages = Object.keys(resources);

  const routes = await Promise.all(
    languages.map(async (value) => {
      const pages = await getPages(value);
      const newsPage = await getNewsPageSlug(value);
      const offresEtServices = await getOffresEtServicesSlug(value);
      const contactSlug = getContactSlug(value as keyof typeof resources);

      const reservedSlugs = [
        contactSlug,
        ...(newsPage ? [newsPage.slug] : []),
        ...(offresEtServices ? [offresEtServices.slug] : []),
      ];

      const pagesForNestedRoutes = excludePagesConflictingWithStaticRoutes(pages, reservedSlugs);
      const children = buildNestedRoutes(pagesForNestedRoutes);

      const staticRoutes: RouteRecord[] = [
        {
          index: true,
          Component: () => <Home />,
          loader: homePageLoader,
          handle: { breadcrumb: 'Accueil' },
        },
      ];

      if (newsPage) {
        staticRoutes.push({
          path: newsPage.slug,
          Component: () => <News />,
          loader: newsPageLoader,
          handle: { breadcrumb: newsPage.seo.breadcrumbTitle || newsPage.seo.metaTitle },
        });
      }

      if (offresEtServices) {
        staticRoutes.push({
          path: offresEtServices.slug,
          Component: () => <OffresEtServices />,
          loader: offresEtServicesLoader,
          handle: {
            breadcrumb: offresEtServices.seo.breadcrumbTitle || offresEtServices.seo.metaTitle,
          },
        });
      }

      staticRoutes.push({
        path: contactSlug,
        Component: () => <Contact />,
        loader: contactLoader,
        handle: { breadcrumb: resources[value as keyof typeof resources].translation.contact.title },
      });

      return {
        path: value === 'fr-FR' ? '/' : `/${value}`,
        Component: App,
        errorElement: <Navigate to="/404" replace />,
        children: [
          ...staticRoutes,
          ...children,
        ],
      };
    })
  );

  return routes;
};

const publicRoutes: RouteRecord[] = await getChildren();

// Add other routes
publicRoutes.push(
  {
    path: '/404',
    Component: App,
    children: [
      {
        index: true,
        Component: () => <ErrorFallback />,
        loader: errorLoader
      }
    ]
  },
);

export default [
  ...publicRoutes,
];
